// The ONE component that renders or edits a task's free-text note. Everything else
// on the board treats the note as opaque and passes it through untouched; this is
// the single seam where a future move to structured per-outlet status would land,
// so keep all note handling here and never parse task.note elsewhere.
//
// Editing is controlled by the board so only one note is open at a time. The editor
// saves on blur (or Enter) and cancels on Escape — no explicit buttons, since a
// daily board shouldn't need three clicks to change a status line.
import { useState, useRef } from 'react';
import { tasksApi } from '../api';
import he from '../i18n/he';

export default function TaskNote({ task, readOnly, editing, onStartEdit, onStopEdit, onSaved }) {
  const [value, setValue] = useState(task.note || '');
  const cancelling = useRef(false);

  if (readOnly) {
    return <div className="task-note-view readonly">{task.note || '—'}</div>;
  }

  if (!editing) {
    // Only this element is clickable — the task title is not — so a click always
    // means "edit the status", never "edit the task".
    return (
      <button
        type="button"
        className={`task-note-view${task.note ? '' : ' empty'}`}
        onClick={() => { setValue(task.note || ''); onStartEdit(); }}
      >
        {task.note || he.board.addNote}
      </button>
    );
  }

  // Both Enter and Escape just blur; the blur handler is the single commit point.
  const finish = async () => {
    if (cancelling.current) { cancelling.current = false; onStopEdit(); return; }
    if (value !== (task.note || '')) {
      await tasksApi.setNote(task.id, value);
      onSaved();
    }
    onStopEdit();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { cancelling.current = true; e.currentTarget.blur(); }
    else if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); e.currentTarget.blur(); }
  };

  return (
    <textarea
      className="task-note-input"
      rows={2}
      autoFocus
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={finish}
      onKeyDown={onKeyDown}
    />
  );
}
